import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const COACH_LABELS = {
  emit: '✨ Emit / SAGE',
  safe: '🛡️ SAFE Coach',
  family_coach: '👨‍👩‍👧 Family Coach',
  mental_performance: '⚡ Mental Performance',
};

const T = {
  pageBg: '#f0f4f8', navy: '#1e3a5f', heading: '#1e293b',
  body: '#475569', bodyLight: '#64748b', accent: '#4f46e5',
  accentLight: '#eef2ff', accentBorder: '#c7d2fe',
  green: '#059669', greenLight: '#ecfdf5', greenBorder: '#a7f3d0',
  amber: '#d97706', amberLight: '#fef3c7', amberBorder: '#fde68a',
  cardBg: '#ffffff', cardBorder: '#e2e8f0',
};

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const PartnerPortal = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [showChangePw, setShowChangePw] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwStatus, setPwStatus] = useState('');
  const apiUrl = window.location.origin;

  useEffect(() => {
    const token = localStorage.getItem('partner_token');
    if (!token) { navigate('/partner-login'); return; }

    fetch(`${apiUrl}/api/v1/partner-portal`, {
      headers: { 'x-partner-token': token }
    })
      .then(r => {
        if (r.status === 401) { localStorage.removeItem('partner_token'); localStorage.removeItem('partner'); navigate('/partner-login'); return null; }
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(d => { if (!d) return; setData(d); setStatus('ready'); })
      .catch(() => setStatus('error'));
  }, []);

  const handleLogout = async () => {
    const token = localStorage.getItem('partner_token');
    await fetch(`${apiUrl}/api/v1/partner-logout`, { method: 'POST', headers: { 'x-partner-token': token } });
    localStorage.removeItem('partner_token');
    localStorage.removeItem('partner');
    navigate('/partner-login');
  };

  const handleChangePw = async () => {
    setPwStatus('');
    if (!pwForm.current || !pwForm.newPw || !pwForm.confirm) { setPwStatus('All fields are required.'); return; }
    if (pwForm.newPw !== pwForm.confirm) { setPwStatus('New passwords do not match.'); return; }
    if (pwForm.newPw.length < 8) { setPwStatus('Password must be at least 8 characters.'); return; }
    const token = localStorage.getItem('partner_token');
    setPwStatus('saving');
    const r = await fetch(`${apiUrl}/api/v1/partner-change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-partner-token': token },
      body: JSON.stringify({ current_password: pwForm.current, new_password: pwForm.newPw })
    });
    const d = await r.json();
    if (r.ok) { setPwStatus('success'); setPwForm({ current: '', newPw: '', confirm: '' }); setTimeout(() => { setShowChangePw(false); setPwStatus(''); }, 2000); }
    else { setPwStatus(d.detail || 'Failed to change password.'); }
  };

  if (status === 'loading') return <div style={{ minHeight: '100vh', background: T.pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.body, fontFamily: "'Libre Franklin', sans-serif" }}>Loading your portal...</div>;
  if (status === 'error') return <div style={{ minHeight: '100vh', background: T.pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.body }}>Error loading portal. Please try again.</div>;

  const { partner, stats } = data;

  return (
    <div style={{ minHeight: '100vh', background: T.pageBg, fontFamily: "'Libre Franklin', sans-serif" }}>

      {/* Header */}
      <div style={{ background: T.navy, padding: '20px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '4px' }}>FHO PLATFORM · PARTNER PORTAL</div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#fff' }}>{partner.name}</div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)', marginTop: '2px', fontFamily: 'monospace', letterSpacing: '0.08em' }}>{partner.code}</div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => setShowChangePw(true)}
            style={{ padding: '9px 20px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
            🔑 Change Password
          </button>
          <button onClick={handleLogout}
            style={{ padding: '9px 20px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
            Sign Out
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '32px 24px' }}>

        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
          {[
            { label: 'Total Members', value: stats.total_members, color: T.accent, bg: T.accentLight, border: T.accentBorder, icon: '👥' },
            { label: 'Active This Month', value: stats.active_members_30d, color: T.green, bg: T.greenLight, border: T.greenBorder, icon: '✅' },
            { label: 'Total Sessions', value: stats.total_sessions, color: T.amber, bg: T.amberLight, border: T.amberBorder, icon: '💬' },
          ].map(s => (
            <div key={s.label} style={{ background: T.cardBg, borderRadius: '16px', padding: '24px', border: `1px solid ${s.border}`, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>{s.icon}</div>
              <div style={{ fontSize: '36px', fontWeight: '700', color: s.color, lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: '13px', color: T.bodyLight, marginTop: '6px' }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>

          {/* Sessions by Coach */}
          <div style={{ background: T.cardBg, borderRadius: '16px', padding: '24px', border: `1px solid ${T.cardBorder}`, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: T.heading, margin: '0 0 18px' }}>Sessions by Coach</h3>
            {Object.keys(stats.sessions_by_coach || {}).length === 0
              ? <p style={{ fontSize: '13px', color: T.bodyLight }}>No sessions recorded yet.</p>
              : Object.entries(stats.sessions_by_coach).map(([coach, count]) => {
                const total = stats.total_sessions || 1;
                const pct = Math.round((count / total) * 100);
                return (
                  <div key={coach} style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '13px', color: T.body }}>{COACH_LABELS[coach] || coach}</span>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: T.navy }}>{count} <span style={{ color: T.bodyLight, fontWeight: '400' }}>({pct}%)</span></span>
                    </div>
                    <div style={{ height: '6px', background: T.pageBg, borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: T.accent, borderRadius: '3px', transition: 'width 0.5s ease' }} />
                    </div>
                  </div>
                );
              })
            }
          </div>

          {/* Weekly Trend */}
          <div style={{ background: T.cardBg, borderRadius: '16px', padding: '24px', border: `1px solid ${T.cardBorder}`, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: T.heading, margin: '0 0 18px' }}>Weekly Signups</h3>
            {(!stats.weekly_trend || stats.weekly_trend.length === 0)
              ? <p style={{ fontSize: '13px', color: T.bodyLight }}>No signup data yet.</p>
              : [...stats.weekly_trend].reverse().map((w, i) => {
                const max = Math.max(...stats.weekly_trend.map(x => x.signups), 1);
                const pct = Math.round((w.signups / max) * 100);
                return (
                  <div key={i} style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                      <span style={{ fontSize: '12px', color: T.bodyLight }}>{w.week}</span>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: T.navy }}>{w.signups}</span>
                    </div>
                    <div style={{ height: '5px', background: T.pageBg, borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: T.green, borderRadius: '3px' }} />
                    </div>
                  </div>
                );
              })
            }
          </div>

          {/* Recent Signups */}
          <div style={{ background: T.cardBg, borderRadius: '16px', padding: '24px', border: `1px solid ${T.cardBorder}`, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: T.heading, margin: '0 0 18px' }}>Recent Members</h3>
            <p style={{ fontSize: '11px', color: T.bodyLight, margin: '0 0 14px', fontStyle: 'italic' }}>Names are anonymized to protect member privacy.</p>
            {(!stats.recent_signups || stats.recent_signups.length === 0)
              ? <p style={{ fontSize: '13px', color: T.bodyLight }}>No members yet.</p>
              : stats.recent_signups.map((s, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${T.cardBorder}` }}>
                  <span style={{ fontSize: '13px', color: T.body, fontFamily: 'monospace' }}>{s.display}</span>
                  <span style={{ fontSize: '12px', color: T.bodyLight }}>{formatDate(s.date)}</span>
                </div>
              ))
            }
          </div>

        </div>

        {/* Change Password Modal */}
      {showChangePw && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ margin: '0 0 20px', color: T.navy, fontSize: '20px' }}>Change Password</h2>
            {[
              { label: 'Current Password', key: 'current', type: 'password' },
              { label: 'New Password', key: 'newPw', type: 'password' },
              { label: 'Confirm New Password', key: 'confirm', type: 'password' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: T.bodyLight, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>{f.label}</label>
                <input type={f.type} value={pwForm[f.key]} onChange={e => setPwForm({ ...pwForm, [f.key]: e.target.value })}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: `1px solid ${T.cardBorder}`, fontSize: '14px', boxSizing: 'border-box', outline: 'none' }} />
              </div>
            ))}
            {pwStatus && pwStatus !== 'saving' && pwStatus !== 'success' && <div style={{ color: '#dc2626', fontSize: '13px', marginBottom: '12px' }}>{pwStatus}</div>}
            {pwStatus === 'success' && <div style={{ color: T.green, fontSize: '13px', marginBottom: '12px' }}>✅ Password changed successfully!</div>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowChangePw(false); setPwStatus(''); setPwForm({ current: '', newPw: '', confirm: '' }); }}
                style={{ padding: '10px 20px', borderRadius: '8px', border: `1px solid ${T.cardBorder}`, background: '#fff', color: T.body, fontSize: '14px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleChangePw} disabled={pwStatus === 'saving'}
                style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: T.accent, color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                {pwStatus === 'saving' ? 'Saving...' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Access Code Box */}
        <div style={{ marginTop: '24px', background: T.accentLight, border: `1px solid ${T.accentBorder}`, borderRadius: '16px', padding: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '700', color: T.accent, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>Your Member Access Code</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: T.navy, fontFamily: 'monospace', letterSpacing: '0.1em' }}>{partner.code}</div>
            <div style={{ fontSize: '12px', color: T.bodyLight, marginTop: '4px' }}>Share this code with your members when they register at freehappyone.com/register</div>
          </div>
          <button onClick={() => navigator.clipboard.writeText(partner.code)}
            style={{ padding: '10px 20px', background: T.accent, border: 'none', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
            Copy Code
          </button>
        </div>

        <p style={{ textAlign: 'center', fontSize: '12px', color: T.bodyLight, marginTop: '32px' }}>
          Questions? Contact <a href="mailto:partnerships@freehappyone.com" style={{ color: T.accent }}>partnerships@freehappyone.com</a>
        </p>

      </div>
    </div>
  );
};

export default PartnerPortal;
